import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import type { ReportTrends } from "./types";

const trendsKey = (businessId: string, reportId: string) => [
  ...reportKey(businessId),
  "trends",
  reportId,
];

/** The newest cached trends for this agent, whatever period it ends with. */
export const cachedTrends = (
  client: QueryClient,
  businessId: string,
  reportId: string,
): ReportTrends | undefined => {
  const [latest] = client
    .getQueryCache()
    .findAll({ queryKey: trendsKey(businessId, reportId) })
    .filter((query) => query.state.data !== undefined)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  return latest
    ? client.getQueryData<ReportTrends>(latest.queryKey)
    : undefined;
};

/**
 * Periods side by side, up to `throughRunId` (the period on screen) or,
 * without it, the latest. The endpoint is still `/trends`. A cached period
 * stands in only so the page can hold that report's chart slots; the tab
 * does not paint its numbers as the week on screen.
 */
export const useInsights = (
  businessId: string,
  reportId: string,
  throughRunId: string | null = null,
  /**
   * Keep a cached period's shape while this one loads. Off for a caller
   * that reads the numbers, which would otherwise draw the wrong week.
   */
  options?: { shape?: boolean },
) => {
  const api = useApi();
  const client = useQueryClient();
  return useQuery<ReportTrends>({
    queryKey: [...trendsKey(businessId, reportId), throughRunId],
    queryFn: ({ signal }) =>
      api
        .get<{ trends: ReportTrends }>(
          `${reportsPath(businessId, reportId)}/trends`,
          {
            params: { run_id: throughRunId ?? undefined },
            signal,
          },
        )
        .then((res) => res.data.trends),
    retry: false,
    placeholderData: options?.shape
      ? () => cachedTrends(client, businessId, reportId)
      : undefined,
  });
};
